import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, SpaceBetween } from '@amzn/awsui-components-react';
import ErrorUtils from 'src/utils/ErrorUtils';
import { NO_DOWNLOAD_LINK_MESSAGE, openDownloadLink } from 'src/utils/openDownloadLink';
import { ApprovalType } from 'src/models/multidimensional-allocation/ProcessApprovals';
import { DerivedAllocationSheetData } from 'src/models/derived-allocation-sheet/DerivedAllocationSheet';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { downloadBlockedReason, submitBlockedReason } from 'src/services/derived-allocation-sheet/dasSheetActions';
import { useApprovalButtonProps } from 'src/components/multidimensional-allocation/hooks/useApprovalButtonProps';
import { ProcessApprovalModal } from 'src/components/multidimensional-allocation/shared/ProcessApprovalModal';

/**
 * Sheet actions under the builder's steps, with the manual allocation sheet's buttons: Download the open version's
 * output, and Submit for Review on a draft or Approval Decision on a sheet under review. Submit waits until the
 * draft is saved and its current content has been executed, and no run is in progress. Submit and the approval decision are offered only on
 * the working version to users who can edit. With edit locking on, Submit also needs the edit lock.
 */
export default function DasSheetActions(props: {
    sheet: DerivedAllocationSheetData,
    canEdit: boolean,
    viewingWorkingVersion: boolean,
    /** Set while the user does not hold the edit lock the draft needs; Submit shows it as the reason it is disabled. */
    submitLockReason?: string,
    /** Called after a submit, approve or reject, to open the working version again (an approve moves the draft to live). */
    onApprovalProcessed: () => void,
}) {
    const { sheet, canEdit, viewingWorkingVersion, submitLockReason, onApprovalProcessed } = props;
    const { dasState, services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    const [modalMode, setModalMode] = useState<'closed' | 'submit' | 'approval'>('closed');
    const [downloading, setDownloading] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const approvalButton = useApprovalButtonProps(sheet.status, () => setModalMode('submit'), () => setModalMode('approval'),
        undefined, () => submitLockReason ?? submitBlockedReason(sheet, dasState.unsavedSteps.length > 0, dasState.executing));
    const downloadReason = downloadBlockedReason(sheet);

    /** Opens the output of the open version through a one-time link. */
    const download = async () => {
        setDownloading(true);
        try {
            const { downloadUrl } = await services.derivedAllocationSheetService.downloadDerivedAllocationSheet({ sheetId, version: sheet.sheetVersion });
            if (downloadUrl) {
                openDownloadLink(downloadUrl);
            } else {
                services.messageService.showErrorBanner(NO_DOWNLOAD_LINK_MESSAGE);
            }
        } catch (ex) {
            services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
        } finally {
            if (mountedRef.current) setDownloading(false);
        }
    };

    return (
        <SpaceBetween direction="horizontal" size="xs">
            <Button data-class="dasDownloadButton" iconName="download" onClick={download} loading={downloading}
                    disabled={!!downloadReason || downloading} disabledReason={downloadReason}>
                Download
            </Button>
            {canEdit && viewingWorkingVersion && (
                <>
                    <Button data-class="dasApprovalButton" variant="primary" onClick={approvalButton.onClick}
                            disabled={approvalButton.disabled} disabledReason={approvalButton.disabledReason}>
                        {approvalButton.label}
                    </Button>
                    <ProcessApprovalModal entityId={sheetId} approvalType={ApprovalType.DERIVED_ALLOCATION_SHEET} mode={modalMode}
                                          onModeChange={setModalMode} onSuccess={onApprovalProcessed} />
                </>
            )}
        </SpaceBetween>
    );
}
